import { jsx as _jsx, jsxs as _jsxs } from "@emotion/react/jsx-runtime";
import { AlertTriangleIcon, CheckNoBoundsCircleIcon, ClearIcon, } from "@fileee/icons";
import { MDCSnackbar } from "@material/snackbar";
import classNames from "classnames";
import React from "react";
import { theme } from "../theme/theme";
import { getSvgColorCss, onCondition } from "../utils/css-utils";
import { NotificationStatus } from "./SnackBarContextNew";
const CssVariables = {
    snackbarBgColor: "--fileee-snackbar-bg-color",
    snackbarDefaultColor: "--fileee-snackbar-default-color",
    snackbarAltColor: "--fileee-snackbar-alt-color",
    snackbarClearIconColor: "--fileee-snackbar-icon-color",
    snackbarButtonHoverOpacity: "--fileee-snackbar-button-hover-opacity",
};
/**
 * The longest dwell time MDC allows. Used for notifications that carry the most
 * to read: errors, and anything with actions attached.
 */
const MAX_TIMEOUT_MS = 10000;
export const SnackBar = ({ notification, onClose, }) => {
    const containerRef = React.useRef(null);
    const { isDarkMode = true, actions, variant, status, message, description, } = notification;
    React.useEffect(() => {
        function closedListener() {
            onClose();
        }
        const snackbar = new MDCSnackbar(containerRef.current);
        if (notification.timeOutMs) {
            snackbar.timeoutMs = notification.timeOutMs;
        }
        else if (status === NotificationStatus.ERROR ||
            (notification.actions && notification.actions.length > 0)) {
            snackbar.timeoutMs = MAX_TIMEOUT_MS;
        }
        else {
            snackbar.timeoutMs = Math.min(Math.max(notification.message.length * 100, 4000), 7000);
        }
        snackbar.open();
        snackbar.listen("MDCSnackbar:closed", closedListener);
        return () => {
            snackbar.unlisten("MDCSnackbar:closed", closedListener);
            snackbar.destroy();
        };
    }, [notification, onClose, status]);
    function handleAction(action) {
        if (action.onAction) {
            action.onAction();
        }
        onClose();
    }
    const containerClass = classNames("mdc-snackbar", {
        "mdc-snackbar--leading": variant === "leading",
        "mdc-snackbar--stacked": variant === "stacked",
    });
    const isErrorMode = status === NotificationStatus.ERROR;
    const textColorClass = isDarkMode && !isErrorMode ? "fTextWhite" : "fText1";
    return (_jsx("div", { className: containerClass, ref: containerRef, css: Object.assign(Object.assign({ top: 0, bottom: "auto", ["& .mdc-snackbar__surface"]: { flexDirection: "row" }, ["& .mdc-snackbar__surface .mdc-snackbar__actions"]: {
                alignSelf: "auto",
            }, ["& .mdc-snackbar__surface .mdc-snackbar__label"]: { padding: 0 }, [CssVariables.snackbarBgColor]: theme.colors.grey700, [CssVariables.snackbarDefaultColor]: theme.colors.primary, [CssVariables.snackbarAltColor]: theme.colors.primary700, [CssVariables.snackbarClearIconColor]: "#ffffff", [CssVariables.snackbarButtonHoverOpacity]: 1 }, onCondition(!isDarkMode, {
            [CssVariables.snackbarBgColor]: "#ffffff",
            [CssVariables.snackbarDefaultColor]: theme.colors.primary700,
            [CssVariables.snackbarAltColor]: theme.colors.primary,
            [CssVariables.snackbarClearIconColor]: theme.colors.grey700,
        })), onCondition(isErrorMode, {
            [CssVariables.snackbarBgColor]: theme.colors.errorSurface,
            [CssVariables.snackbarDefaultColor]: theme.colors.errorStrong,
            [CssVariables.snackbarAltColor]: theme.colors.errorText,
            [CssVariables.snackbarClearIconColor]: theme.colors.text2,
            [CssVariables.snackbarButtonHoverOpacity]: 0.8,
        })), children: _jsxs("div", { className: "mdc-snackbar__surface pa3", css: Object.assign({ width: "100%", maxWidth: 640, minHeight: 56, borderRadius: 8, alignItems: "flex-start", backgroundColor: `var(${CssVariables.snackbarBgColor})` }, onCondition(!isDarkMode || isErrorMode, {
                border: `2px solid var(${CssVariables.snackbarDefaultColor})`,
            })), children: [_jsx("div", { css: { height: 24 }, children: isErrorMode ? (_jsx(AlertTriangleIcon, { css: getSvgColorCss(`var(${CssVariables.snackbarDefaultColor})`) })) : (_jsx(CheckNoBoundsCircleIcon, { css: getSvgColorCss(`var(${CssVariables.snackbarDefaultColor})`) })) }), _jsxs("div", { css: { flexGrow: 1, wordBreak: "break-word" }, className: `${textColorClass} pl3`, children: [_jsx("div", { className: `${textColorClass} mdc-snackbar__label u-fMedium fi:text-body2`, role: "status", "aria-live": "polite", children: message }), description && (_jsx("div", { className: "mt2 fi:text-body2", children: description })), actions && actions.length > 0 && (_jsx("div", { className: "mt2", children: actions.map((action, index) => (_jsx("button", { type: "button", css: {
                                    display: "inline-flex",
                                    backgroundColor: "transparent",
                                    border: "none",
                                    outline: "none",
                                    color: `var(${CssVariables.snackbarDefaultColor})`,
                                    ["&:hover , &:focus"]: {
                                        backgroundColor: "transparent",
                                        color: `var(${CssVariables.snackbarAltColor})`,
                                        textDecoration: "underline",
                                        opacity: parseFloat(`var(${CssVariables.snackbarButtonHoverOpacity})`),
                                    },
                                }, className: `${index !== 0 && "ml3"} fi:p-0`, onClick: () => handleAction(action), children: action.label }, index))) }))] }), _jsx("div", { className: "mdc-snackbar__actions", css: { height: 24, width: 24 }, children: _jsx("button", { type: "button", className: "mdc-icon-button mdc-snackbar__dismiss", "aria-label": "Close", onClick: onClose, children: _jsx(ClearIcon, { "aria-hidden": "true", css: getSvgColorCss(`var(${CssVariables.snackbarClearIconColor})`) }) }) })] }) }));
};
//# sourceMappingURL=SnackBar.js.map